import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Banknote, Scale, Hammer, ShieldCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LeadGenWidgets — three opt-in capture cards on the property detail page.
 *
 * Buyer-driven (mortgage / solicitor / surveyor). Generates revenue from
 * adjacent service providers without competing with the agent subscription.
 */
const LeadGenWidgets = ({ property }) => {
  const propertyId = property?._id || property?.id;
  const propertyPrice = property?.price;
  const country = property?.location?.country || property?.country || '';
  const isUK = /United Kingdom|UK|Great Britain|England|Scotland|Wales|Northern Ireland/i.test(country);
  const isUS = /United States|USA|US\b/i.test(country);
  const providerAdj = isUK ? 'UK' : isUS ? 'US' : 'Irish';
  const solicitorNoun = isUK ? 'solicitors / conveyancers' : isUS ? 'real-estate attorneys' : 'property solicitors';

  return (
    <Card data-testid="leadgen-widgets">
      <CardContent className="p-6 space-y-1">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-5 w-5 text-[#7042cc]" />
          <h3 className="text-lg font-bold text-gray-900">Get this property over the line</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Free, no-obligation quotes from verified {providerAdj} providers. We&apos;ll match you and you decide.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <LeadCard
            type="mortgage"
            icon={<Banknote className="h-5 w-5" />}
            title="Mortgage quote"
            blurb={`Compare rates from leading ${providerAdj} lenders for this property.`}
            propertyId={propertyId}
            propertyPrice={propertyPrice}
            tone="blue"
          />
          <LeadCard
            type="solicitor"
            icon={<Scale className="h-5 w-5" />}
            title={isUS ? 'Real-estate attorney' : 'Conveyancing solicitor'}
            blurb={`Fixed-fee quotes from experienced ${providerAdj} ${solicitorNoun}.`}
            propertyId={propertyId}
            propertyPrice={propertyPrice}
            tone="emerald"
          />
          <LeadCard
            type="surveyor"
            icon={<Hammer className="h-5 w-5" />}
            title={isUS ? 'Home inspector' : 'Surveyor / valuer'}
            blurb="Pre-purchase structural surveys with detailed reports."
            propertyId={propertyId}
            propertyPrice={propertyPrice}
            tone="amber"
          />
        </div>
      </CardContent>
    </Card>
  );
};

const LeadCard = ({ type, icon, title, blurb, propertyId, propertyPrice, tone }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', consent: false });

  const tones = {
    blue: 'border-[#7042cc]/20 hover:border-[#7042cc]/50 text-[#5b35a8] bg-[#F5EFFF]',
    emerald: 'border-emerald-200 hover:border-emerald-400 text-emerald-700 bg-emerald-50',
    amber: 'border-amber-200 hover:border-amber-400 text-amber-700 bg-amber-50',
  }[tone];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast({ title: 'Please tick the consent box', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, {
        type,
        name: form.name,
        email: form.email,
        phone: form.phone,
        property_id: propertyId || '',
        property_price: propertyPrice || null,
        consent: true,
        message: '',
      });
      setSuccess(true);
      toast({ title: 'Request received', description: 'A verified provider will be in touch shortly.' });
    } catch (err) {
      toast({
        title: 'Could not submit',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`border rounded-lg p-4 transition-colors ${tones}`} data-testid={`leadgen-${type}`}>
      <div className="flex items-center gap-2 text-gray-900 font-semibold mb-1">
        {icon}
        {title}
      </div>
      <p className="text-xs text-gray-600 mb-3">{blurb}</p>

      {success ? (
        <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-semibold" data-testid={`leadgen-${type}-success`}>
          <CheckCircle2 className="h-3.5 w-3.5" /> Request received — check your email
        </div>
      ) : !open ? (
        <Button
          size="sm"
          variant="outline"
          className="w-full bg-white"
          onClick={() => setOpen(true)}
          data-testid={`leadgen-${type}-open`}
        >
          Get a free quote
        </Button>
      ) : (
        <form onSubmit={submit} className="space-y-2 mt-1">
          <div>
            <Label htmlFor={`${type}-name`} className="text-[11px]">Name</Label>
            <Input id={`${type}-name`} required className="bg-white h-8 text-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label htmlFor={`${type}-email`} className="text-[11px]">Email</Label>
            <Input id={`${type}-email`} type="email" required className="bg-white h-8 text-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <Label htmlFor={`${type}-phone`} className="text-[11px]">Phone (optional)</Label>
            <Input id={`${type}-phone`} className="bg-white h-8 text-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <label className="flex items-start gap-2 text-[11px] text-gray-700 leading-snug pt-1">
            <Checkbox
              checked={form.consent}
              onCheckedChange={(v) => setForm({ ...form, consent: !!v })}
              data-testid={`leadgen-${type}-consent`}
            />
            <span>
              I'm happy for lovinahome to share these details with a verified {type} provider so they can contact me with a quote.
            </span>
          </label>
          <div className="flex gap-2 pt-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" size="sm" className="flex-1 bg-[#7042cc] hover:bg-[#5b35a8]" disabled={submitting} data-testid={`leadgen-${type}-submit`}>
              {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Send'}
            </Button>
          </div>
        </form>
      )}
      <div className="mt-2">
        <Badge variant="outline" className="text-[9px] py-0 px-1 bg-white">No spam • GDPR opt-in</Badge>
      </div>
    </div>
  );
};

export default LeadGenWidgets;
